import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { formatTimeAgo } from "@redotech/util/date-format";
import { useMemo } from "react";

export interface UseSupportTicketCardProps {
  supportTicket: SupportTicketListItemResponse;
}

export interface UseSupportTicketCardResult {
  channels: readonly MessageChannel[];
  isUnread: boolean;
  timeSinceMessage: string;
  lastMessageDate: Date | null;
}

export function isSupportTicketUnread(
  supportTicket: SupportTicketListItemResponse,
): boolean {
  return (
    supportTicket.lastCustomerMessageAt !== null &&
    (supportTicket.readAt === null ||
      supportTicket.lastCustomerMessageAt > supportTicket.readAt)
  );
}

export function useSupportTicketCard({
  supportTicket,
}: UseSupportTicketCardProps): UseSupportTicketCardResult {
  const { isUnread, timeSinceMessage, lastMessageDate } = useMemo(() => {
    const isUnread = isSupportTicketUnread(supportTicket);

    // Use the most recent message time (customer or merchant)
    const lastMessageDate =
      getMoreRecent(
        supportTicket.lastCustomerMessageAt,
        supportTicket.lastMerchantMessageAt,
      ) ?? null;

    const timeSinceMessage = lastMessageDate
      ? formatTimeAgo(lastMessageDate.toISOString())
      : "";

    return { isUnread, timeSinceMessage, lastMessageDate };
  }, [supportTicket]);

  return {
    channels: supportTicket.channels,
    isUnread,
    timeSinceMessage,
    lastMessageDate,
  };
}

function getMoreRecent(a: Date | null, b: Date | null): Date | null {
  if (a === null) {
    return b;
  }
  if (b === null) {
    return a;
  }
  return a > b ? a : b;
}
